import React, { useState, useEffect } from 'react';
import { Upload, FileText, Trash2, Download, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const ResultadosTab = () => {
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [veterinarios, setVeterinarios] = useState([]);
  const [formData, setFormData] = useState({
    veterinario_email: '',
    paciente_nombre: '',
    estudio_nombre: '',
    notas: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resultadosResponse, vetsResponse] = await Promise.all([
        api.get('/resultados/all'),
        api.get('/admin/users')
      ]);
      setResultados(resultadosResponse.data);
      // Filtrar solo veterinarios aprobados
      const approvedVets = vetsResponse.data.filter(u => u.is_approved && !u.is_admin);
      setVeterinarios(approvedVets);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFormData({...formData, file});
    } else {
      toast({
        title: 'Error',
        description: 'Solo se permiten archivos PDF',
        variant: 'destructive',
      });
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      toast({
        title: 'Error',
        description: 'Debes seleccionar un archivo PDF',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('veterinario_email', formData.veterinario_email);
    uploadFormData.append('paciente_nombre', formData.paciente_nombre);
    uploadFormData.append('estudio_nombre', formData.estudio_nombre);
    uploadFormData.append('notas', formData.notas || '');
    uploadFormData.append('file', formData.file);

    try {
      await api.post('/resultados/upload', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast({
        title: '✅ Resultado subido',
        description: 'El PDF ha sido subido exitosamente',
      });
      
      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo subir el resultado',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (resultadoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este resultado? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      await api.delete(`/resultados/${resultadoId}`);
      toast({
        title: 'Resultado eliminado',
        description: 'El resultado ha sido eliminado exitosamente',
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el resultado',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      veterinario_email: '',
      paciente_nombre: '',
      estudio_nombre: '',
      notas: '',
      file: null
    });
  };

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-[#1e3a8a]">📄 Gestión de Resultados (PDFs)</CardTitle>
          <p className="text-sm text-gray-600">Total de resultados: {resultados.length}</p>
        </div>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#84cc16] hover:bg-[#65a30d]"
          >
            <Upload className="h-4 w-4 mr-2" />
            Subir Resultado
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {showForm && (
          <Card className="mb-6 border-2 border-[#84cc16]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Subir Nuevo Resultado</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Veterinario *</label>
                    <select
                      value={formData.veterinario_email}
                      onChange={(e) => setFormData({...formData, veterinario_email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Seleccionar veterinario</option>
                      {veterinarios.map((vet) => (
                        <option key={vet.id} value={vet.email}>
                          {vet.nombre} ({vet.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre del Paciente *</label>
                    <Input
                      value={formData.paciente_nombre}
                      onChange={(e) => setFormData({...formData, paciente_nombre: e.target.value})}
                      placeholder="Ej: Firulais"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre del Estudio *</label>
                    <Input
                      value={formData.estudio_nombre}
                      onChange={(e) => setFormData({...formData, estudio_nombre: e.target.value})}
                      placeholder="Ej: Hemograma Completo"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Archivo PDF *</label>
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Notas (Opcional)</label>
                  <textarea
                    value={formData.notas}
                    onChange={(e) => setFormData({...formData, notas: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    rows="3"
                    placeholder="Notas adicionales sobre el resultado..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="submit" 
                    className="bg-[#1e40af] hover:bg-[#1e3a8a]"
                    disabled={uploading}
                  >
                    {uploading ? 'Subiendo...' : 'Subir Resultado'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {resultados.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p>No hay resultados subidos</p>
          </div>
        ) : (
          <div className="space-y-3">
            {resultados.map((resultado) => (
              <Card key={resultado.id} className="border-2">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="h-5 w-5 text-[#1e40af]" />
                        <h3 className="font-bold text-lg text-[#1e3a8a]">{resultado.estudio_nombre}</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                        <div>
                          <span className="text-gray-500">Paciente:</span>
                          <span className="ml-2 font-medium">{resultado.paciente_nombre}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Veterinario:</span>
                          <span className="ml-2 font-medium">{resultado.veterinario_nombre}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <span className="ml-2 font-medium text-xs">{resultado.veterinario_email}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Subido por:</span>
                          <span className="ml-2 font-medium">{resultado.subido_por_nombre}</span>
                        </div>
                      </div>
                      {resultado.notas && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <span className="font-semibold">Notas:</span> {resultado.notas}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-gray-500">
                        Fecha: {new Date(resultado.created_at).toLocaleString('es-MX')}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(resultado.pdf_url, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Ver PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(resultado.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ResultadosTab;
